import React from 'react';
import { usePricingFormContext } from './PricingFormContext';
import { Col, Row, Layout, Divider, InputNumber } from 'antd';

/** 燃油附加费费用项code：金额 = 基础运费(1001001001) × 燃油费率(%) */
export const FUEL_SURCHARGE_FEE_ITEM_CODE = 1001001062;
export const FUEL_SURCHARGE_RATE_MIN = 0;
export const FUEL_SURCHARGE_RATE_MAX = 100;

const headerStyle: React.CSSProperties = {
    color: '#090909ff',
    lineHeight: '25px',
    backgroundColor: '#fff',
    paddingLeft: '0px',
    paddingRight: '0px',
};
const dividerStyle: React.CSSProperties = {
    margin: '15px',
};

export interface FuelSurchargeRateCard {
    /** 燃油费率（%），0.00 ~ 100.00，最多两位小数 */
    fuelSurchargeRate?: number | null;
}

export interface FuelSurchargeRateData {
    cards: FuelSurchargeRateCard[];
}

/** 校验燃油费率：必填、0~100、最多两位小数；通过返回 null，否则返回错误文案 */
export const validateFuelSurchargeRate = (rate: number | null | undefined): string | null => {
    if (rate === null || rate === undefined || Number.isNaN(Number(rate))) {
        return '请填写燃油费率';
    }
    const value = Number(rate);
    if (value < FUEL_SURCHARGE_RATE_MIN || value > FUEL_SURCHARGE_RATE_MAX) {
        return '燃油费率必须在 0.00% ~ 100.00% 之间';
    }
    const str = value.toString();
    const decimalIndex = str.indexOf('.');
    if (decimalIndex !== -1 && str.length - decimalIndex - 1 > 2) {
        return '燃油费率最多支持两位小数';
    }
    return null;
};

const FuelSurchargeRateTabb: React.FC<{
    tabId: string;
    data?: FuelSurchargeRateData;
    onChange?: (data: FuelSurchargeRateData) => void;
    feeItemCodeNum?: number;
    onDataChange?: (code: number, data: FuelSurchargeRateData) => void;
    disabled?: boolean;
}> = ({ data: dataProp, onChange, feeItemCodeNum, onDataChange, disabled = false }) => {
    const { Header, Content } = Layout;
    const ctx = usePricingFormContext();
    const data = dataProp ?? (feeItemCodeNum != null ? ctx?.feeItemDataMap?.get(feeItemCodeNum) : undefined);
    const card: FuelSurchargeRateCard = data?.cards?.[0] || { fuelSurchargeRate: null };

    const notify = React.useCallback((payload: FuelSurchargeRateData) => {
        if (onDataChange != null && feeItemCodeNum != null) onDataChange(feeItemCodeNum, payload);
        else onChange?.(payload);
    }, [onDataChange, onChange, feeItemCodeNum]);

    return (
        <Layout style={{ backgroundColor: '#fff', padding: 15, border: '1px solid #e8e8e8', borderRadius: 5 }}>
            <Header style={headerStyle}>
                <Row justify="start" align="middle">
                    <Col span={24}>
                        <span>Fuel Surcharge Rate/燃油费率</span>
                    </Col>
                </Row>
                <Divider style={dividerStyle} />
            </Header>
            <Content style={{ background: '#fff' }}>
                <Row justify="start" gutter={[16, 0]} align="middle">
                    <Col>
                        <span>燃油附加费 = 基础运费 ×</span>
                    </Col>
                    <Col>
                        <InputNumber
                            size="small"
                            style={{ width: 120 }}
                            min={FUEL_SURCHARGE_RATE_MIN}
                            max={FUEL_SURCHARGE_RATE_MAX}
                            precision={2}
                            step={0.01}
                            addonAfter="%"
                            value={card.fuelSurchargeRate ?? undefined}
                            disabled={disabled}
                            onChange={(v) => notify({ cards: [{ fuelSurchargeRate: v ?? null }] })}
                            placeholder="0.00"
                        />
                    </Col>
                </Row>
                <Row style={{ marginTop: 8, color: '#999' }}>
                    <Col span={24}>
                        <span>费率范围 0.00% ~ 100.00%，最多两位小数；仅基础运费参与计算，需同时配置基础运费</span>
                    </Col>
                </Row>
            </Content>
        </Layout>
    );
};

export default React.memo(FuelSurchargeRateTabb);
